/**
 * ============================================================
 * KEJJA OS — src/lib/settingsApi.ts + systemApi.ts (combined)
 * ============================================================
 */

import { api } from '@/lib/api';

// ============================================================
// Settings types
// ============================================================

export interface UserProfile {
  id: string;
  email: string | null;
  name: string | null;
  displayName: string | null;
  avatarEmoji: string | null;
  motto: string | null;
  theme: string | null;
}

export interface HomePreferences {
  homeId: string;
  timezone: string;
  currencyCode: string;
  language: string;
  theme: string;
  notificationsEnabled: boolean;
  modulePrefs: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface HomeMember {
  userId: string;
  email: string | null;
  name: string | null;
  displayNickname: string | null;
  displayName: string | null;
  avatarEmoji: string | null;
  role: string;
  joinedAt: string | null;
}

export interface HomeInvite {
  id: string;
  homeId: string;
  invitedByUserId: string | null;
  inviteCode: string;
  role: string;
  note: string | null;
  maxUses: number;
  useCount: number;
  expiresAt: string | null;
  createdAt: string;
  deletedAt: string | null;
}

// ============================================================
// System types
// ============================================================

export interface VersionInfo {
  app: { name: string; version: string };
  backend: { node: string; platform: string; arch: string };
  runtime: { uptimeSeconds: number; memoryMB: number };
  deployedAt: string;
}

export interface ActivityEntry {
  id: string;
  homeId: string | null;
  userId: string | null;
  module: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  summary: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface IntegrationStatus {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'inactive' | 'error' | 'planned';
  detail?: string;
}

// ============================================================
// Settings API
// ============================================================

export interface ProfileUpdatePayload {
  displayName?: string | null;
  avatarEmoji?: string | null;
  motto?: string | null;
  theme?: 'system' | 'light' | 'dark' | null;
}

export interface PrefsUpdatePayload {
  timezone?: string;
  currencyCode?: string;
  language?: string;
  theme?: 'system' | 'light' | 'dark';
  notificationsEnabled?: boolean;
  modulePrefs?: Record<string, unknown>;
}

export interface HomeDetailsPayload {
  name?: string;
  motto?: string | null;
}

export interface CreateInvitePayload {
  role?: string;
  note?: string | null;
  maxUses?: number;
  expiresInDays?: number | null;
}

export const settingsApi = {
  getProfile: () => api.get<{ profile: UserProfile }>('/settings/profile'),
  updateProfile: (body: ProfileUpdatePayload) =>
    api.patch<{ profile: UserProfile }>('/settings/profile', body),

  acceptInvite: (inviteCode: string) =>
    api.post<{ homeId: string; homeName: string }>('/settings/invites/accept', { inviteCode }),

  getPreferences: (homeId: string) =>
    api.get<{ preferences: HomePreferences }>(`/homes/${homeId}/settings/preferences`),
  updatePreferences: (homeId: string, body: PrefsUpdatePayload) =>
    api.patch<{ preferences: HomePreferences }>(`/homes/${homeId}/settings/preferences`, body),

  updateHomeDetails: (homeId: string, body: HomeDetailsPayload) =>
    api.patch<{ ok: boolean }>(`/homes/${homeId}/settings/home`, body),

  listMembers: (homeId: string) =>
    api.get<{ members: HomeMember[] }>(`/homes/${homeId}/settings/members`),
  changeRole: (homeId: string, userId: string, role: string) =>
    api.patch<{ ok: boolean }>(`/homes/${homeId}/settings/members/${userId}/role`, { role }),
  removeMember: (homeId: string, userId: string) =>
    api.delete<void>(`/homes/${homeId}/settings/members/${userId}`),
  leaveHome: (homeId: string) =>
    api.post<void>(`/homes/${homeId}/settings/members/leave`, {}),

  listInvites: (homeId: string) =>
    api.get<{ invites: HomeInvite[] }>(`/homes/${homeId}/settings/invites`),
  createInvite: (homeId: string, body: CreateInvitePayload) =>
    api.post<{ invite: HomeInvite }>(`/homes/${homeId}/settings/invites`, body),
  revokeInvite: (homeId: string, inviteId: string) =>
    api.delete<void>(`/homes/${homeId}/settings/invites/${inviteId}`),
};

// ============================================================
// System API
// ============================================================

export const systemApi = {
  getVersion: () => api.get<VersionInfo>('/system/version'),

  listActivity: (homeId: string, limit = 50) =>
    api.get<{ activity: ActivityEntry[] }>(`/homes/${homeId}/system/activity?limit=${limit}`),

  getIntegrations: (homeId: string) =>
    api.get<{ integrations: IntegrationStatus[] }>(`/homes/${homeId}/system/integrations`),

  exportUrl: (homeId: string) => `/api/v1/homes/${homeId}/system/export`,

  deleteHome: (homeId: string) =>
    api.post<{ ok: boolean }>(`/homes/${homeId}/system/danger/delete-home`, { confirm: 'DELETE THIS HOME' }),

  deleteAccount: () =>
    api.post<{ ok: boolean; message: string }>('/system/account/delete', { confirm: 'DELETE MY ACCOUNT' }),
};
